/**
 * Cover queries used by alert-state guard pathing. Reads the map only —
 * no PixiJS. See CLAUDE.md > Guard AI ("cover preference") and
 * CLAUDE.md > Vision (half walls + cubicle partitions).
 */

import { inBounds, tileAt, type MapData } from './map';
import { isCoverTile, TileType } from './tiles';

/**
 * The "foot shadow" a standing observer casts past cover. A standing observer
 * sees OVER cover tiles (cubicle partitions, half walls) into the room beyond,
 * but the single tile directly behind each visible cover tile — one step
 * further from the observer along the line of sight — is shadowed. This gives
 * cover a one-tile concealment margin (a croucher hugging the back of the wall
 * stays hidden) without hiding the whole room. Mutates `fov` in place; call
 * only for a STANDING observer (a crouched one treats cover as fully opaque).
 */
export function coverShadowMargin(
  map: MapData,
  ox: number,
  oy: number,
  fov: Set<number>,
): void {
  const behind: number[] = [];
  for (const idx of fov) {
    const x = idx % map.width;
    const y = (idx - x) / map.width;
    if (!isCoverTile(tileAt(map, x, y))) continue;
    // One tile further from the observer along the sightline.
    const bx = x + Math.sign(x - ox);
    const by = y + Math.sign(y - oy);
    if (bx === x && by === y) continue; // observer sitting on the cover (can't happen)
    behind.push(by * map.width + bx);
  }
  for (const idx of behind) fov.delete(idx);
}

/**
 * True if a Bresenham line between (ax, ay) and (bx, by) passes through
 * any partial-cover tile (cubicle partition or half wall) in its interior.
 * Walls and Void are NOT counted — those fully block LOS and shooting,
 * so hiding behind them wouldn't let the guard peek out.
 */
export function hasCoverBetween(
  map: MapData,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): boolean {
  if (ax === bx && ay === by) return false;
  const dx = Math.abs(bx - ax);
  const dy = Math.abs(by - ay);
  const sx = ax < bx ? 1 : -1;
  const sy = ay < by ? 1 : -1;
  let err = dx - dy;
  let x = ax;
  let y = ay;
  while (true) {
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x += sx;
    }
    if (e2 < dx) {
      err += dx;
      y += sy;
    }
    if (x === bx && y === by) return false;
    if (!inBounds(map, x, y)) return false;
    const t = tileAt(map, x, y);
    if (t === TileType.CubiclePartition || t === TileType.HalfWall) return true;
  }
}

/**
 * A* per-tile cost bias that penalizes tiles without cover from the given
 * shooter — used when an alert guard pathfinds toward the player so they
 * naturally route through cubicles and half-walls instead of sprinting
 * down open aisles.
 */
export function coverCostBias(
  map: MapData,
  shooterX: number,
  shooterY: number,
  openPenalty: number,
): (x: number, y: number) => number {
  return (x, y) => (hasCoverBetween(map, x, y, shooterX, shooterY) ? 0 : openPenalty);
}
